import { ApolloError, useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { FC, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import Button from '~/components/Button';
import Icon from '~/components/Icon';
import { Identifiers } from '~/constants/enum';
import { QUERY_GET_CMS_BLOCKS } from '~/data/home';
import { ADD_PRODUCT_REVIEW, GET_REVIEW_META_DATA } from '~/data/product/gql';
import { UserAtom } from '~/services/user';
import { isValidEmail } from '~/utils/validators';
import styles from './StepReview.module.scss';
import StepReviewInfo from './step-info';
import StepReviewRate from './step-rate';
import StepReviewThanks from './step-thanks';
import StepReviewUpload from './step-upload';
import StepReviewWrite from './step-write';

interface FormValues {
    email_address: string;
    name: string;
    content?: string;
    files?: IFile[];
    star?: number;
    summary?: string;
}

type StepReviewProps = {
    sku?: string;
    onCancel?: () => void;
};

enum EnumStepReview {
    Rate = 'rate-review',
    Upload = 'upl-review',
    Write = 'write-review',
    Info = 'info-review',
    Thanks = 'thanks-review'
}

export const tabs = [
    {
        id: EnumStepReview.Rate,
        content: StepReviewRate
    },
    {
        id: EnumStepReview.Upload,
        content: StepReviewUpload
    },
    {
        id: EnumStepReview.Write,
        content: StepReviewWrite
    },
    {
        id: EnumStepReview.Info,
        content: StepReviewInfo
    },
    {
        id: EnumStepReview.Thanks,
        content: StepReviewThanks
    }
];

export const PrevBtn: FC<any> = ({ onClick, currentSlide, form }) => {
    return (
        <div
            style={{ position: 'absolute', bottom: -10, zIndex: 999 }}
            onClick={onClick}
            className={classNames([0, 4].includes(currentSlide) && 'opacity-0 invisible')}
        >
            <div className="cursor-pointer font-semibold px-2 py-1">
                <div className="flex gap-2 items-center">
                    <Button
                        variant="secondary"
                        type="outline"
                        style={{ width: 96 }}
                        prefixIcon={<Icon name="arrow-left" size={24} />}
                        size="small"
                    >
                        Back
                    </Button>
                </div>
            </div>
        </div>
    );
};

export const NextBtn: FC<any> = ({ onClick, currentSlide, form, onCancel }) => {
    const disabled =
        (currentSlide === 2 && form?.values?.content?.trim() === '') ||
        (currentSlide === 3 && form?.values?.summary?.trim() === '');

    return (
        <div
            style={{ position: 'absolute', bottom: -10, right: 0, zIndex: 999 }}
            onClick={
                currentSlide === 4 ? onCancel : currentSlide === 3 ? form?.submitForm : !disabled ? onClick : () => {}
            }
            className={classNames(currentSlide === 0 && 'opacity-0 invisible')}
        >
            <div className="cursor-pointer font-semibold px-2 py-1">
                <div className="flex gap-2 items-center">
                    {currentSlide === 1 && form?.values?.files?.length === 0 ? (
                        'Skip'
                    ) : (
                        <Button
                            variant={currentSlide == 4 ? 'secondary' : 'primary'}
                            style={{ width: 96 }}
                            disabled={disabled || !!form?.isSubmitting}
                            size="small"
                            loading={form?.isSubmitting}
                        >
                            {currentSlide === 3 ? 'Done' : currentSlide === 4 ? 'Close' : 'Next'}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

const arrowRender = (form?: any, type?: 'next' | 'prev', onCancel?: any) => {
    if (type === 'prev') {
        return <PrevBtn form={form} />;
    } else {
        return <NextBtn form={form} onCancel={onCancel} />;
    }
};

const StepReview = ({ sku, onCancel }: StepReviewProps) => {
    const currentUser = useRecoilValue(UserAtom.currentUser);

    const { data } = useQuery(QUERY_GET_CMS_BLOCKS, {
        variables: {
            identifiers: [Identifiers.ReviewCodeDiscount]
        }
    });

    const dataDiscount: any = useMemo(() => {
        const parser = new DOMParser();
        if (!data?.cmsBlocks?.items?.[0]?.content) return {};
        const doc = parser.parseFromString(data?.cmsBlocks?.items?.[0]?.content, 'text/html');
        if (!doc) return {};
        // Lấy giá trị dòng text và code
        const discountText = doc.querySelector('#discount-text')?.textContent;
        const discountCode = doc.querySelector('#discount-code')?.textContent;
        return {
            [EnumStepReview.Upload]: {
                discountText
            },
            [EnumStepReview.Thanks]: {
                discountCode
            }
        };
    }, [data]);

    let sliderRef = useRef(null) as any;
    const [currentSlick, setCurrentSlick] = useState<number>(0);
    const [oldIndex, setOldIndex] = useState<number | undefined>();

    const errorHandler = ({ message }: ApolloError) => toast.error(message);

    const onSlickNext = () => {
        sliderRef.slickNext();
    };

    const [addProductReview, { called, error }] = useMutation(ADD_PRODUCT_REVIEW, {
        onError: errorHandler,
        onCompleted: () => {
            onSlickNext();
        }
    });

    const form = useFormik<FormValues>({
        initialValues: {
            email_address: currentUser?.email || '',
            name: (currentUser?.firstname || '') + ' ' + (currentUser?.lastname || '') || '',
            content: '',
            summary: currentUser?.email,
            files: [],
            star: 0
        },
        validate: (values) => {
            const errors: Record<string, string> = {};
            // if (!values.name?.trim()) {
            //   errors.name = "Please enter your name";
            // }
            if (!values.content?.trim()) {
                errors.content = 'Please enter your review content';
            }
            if (!values.summary?.trim()) {
                errors.summary = 'Please enter your review title';
            } else if (!isValidEmail(values.summary)) {
                errors.summary = 'Email is not valid';
            }
            if (!values?.star) {
                errors.star = 'Please enter your rating';
            }

            return errors;
        },
        onSubmit: async (values) => {
            const { name, content, summary } = values;

            const ratingValue = reviewMetaDataValue
                ?.slice(0)
                .find((item: any, index: number) => index + 1 === values.star)?.value_id;
            const images = values?.files?.map((file) => file.url) || [];
            return await addProductReview({
                variables: {
                    sku,
                    nickname: name?.trim() || summary,
                    summary: summary,
                    text: content,
                    ratingsId: ratingsId,
                    ratingsValueId: ratingValue,
                    images
                }
            });
        }
    });

    const { data: reviewMeta } = useQuery(GET_REVIEW_META_DATA, {
        fetchPolicy: 'network-only'
    });

    const reviewMetaDataValue = reviewMeta?.productReviewRatingsMetadata?.items?.[0]?.values;
    const ratingsId = reviewMeta?.productReviewRatingsMetadata?.items?.[0]?.id;

    // const newTabs = useMemo(() => {
    //     return tabs.map((tab) => {
    //         const Component = tab.content;
    //         return {
    //             ...tab,
    //             content: <Component form={form} onSlickNext={onSlickNext} />
    //         };
    //     });
    // }, [tabs, form]);

    const onCloseModal = () => {
        onCancel?.();
        form.resetForm();
    };

    return (
        <div className={styles.tab_review_dots}>
            <div>
                <Slider
                    speed={800}
                    infinite={false}
                    arrows={true}
                    prevArrow={arrowRender(form, 'prev', onCloseModal)}
                    nextArrow={arrowRender(form, 'next', onCloseModal)}
                    swipeToSlide={true}
                    slidesToShow={1}
                    // lazyLoad="anticipated"
                    customPaging={(i) => {
                        return (
                            <div
                                className={classNames(
                                    styles.meter,
                                    'rounded',
                                    [0, 4].includes(currentSlick) && 'opacity-0 invisible',
                                    'hidden lg:block'
                                )}
                            >
                                {i !== 4 && (
                                    <>
                                        <span className={`h-[8px] w-36  bg-[#e2e2e2] cursor-pointer`} />
                                        <span
                                            className={classNames(
                                                i <= currentSlick
                                                    ? styles.progress
                                                    : oldIndex === i && styles.progress_out,
                                                ''
                                            )}
                                        />
                                    </>
                                )}
                            </div>
                        );
                    }}
                    dotsClass={classNames(
                        styles.dots_class,
                        '!flex gap-2 justify-center items-center absolute bottom-0 left-1/2 transform -translate-x-1/2'
                    )}
                    className="mb-[10px] modal-review-react-slick"
                    beforeChange={(oldIndex, newIndex) => {
                        setOldIndex(oldIndex);
                        setCurrentSlick(newIndex);
                    }}
                    ref={(slider) => {
                        sliderRef = slider;
                    }}
                    swipe={false}
                >
                    {tabs.map((tab, index) => {
                        const Component = tab.content;
                        if (tab.id === EnumStepReview.Thanks && !called) {
                            return null;
                        }
                        const data = dataDiscount?.[tab.id as EnumStepReview] || {};
                        return (
                            <div className={`flex justify-center items-center lg:px-20`} key={index}>
                                <div className={`${[0, 4].includes(index) ? 'pt-8' : 'pb-14'}`}>
                                    {<Component form={form} onSlickNext={onSlickNext} {...data} />}
                                </div>
                            </div>
                        );
                    })}
                </Slider>
            </div>
        </div>
    );
};

export default StepReview;
